import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight, Mountain, Users, DollarSign, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { UsePricingCalculatorReturn } from '@/hooks/communities/montana';
import { formatCurrency, formatNumber } from '@/utils/entities/city/montana';

interface MontanaPricingHeroProps {
  onGetStarted?: () => void;
  onContactSales?: () => void;
  onScheduleDemo?: () => void;
  pricingState: UsePricingCalculatorReturn;
  // Optional: names of selected add-ons to summarize in hero
  selectedAddOnNames?: string[];
}

export const MontanaPricingHero: React.FC<MontanaPricingHeroProps> = ({
  onGetStarted,
  onContactSales,
  onScheduleDemo,
  pricingState,
  selectedAddOnNames,
}) => {
  const { parcels, setParcels, pricing } = pricingState;

  // Parse parcels string to number (remove commas) for safe display
  const parcelsNumber = parseInt(parcels.toString().replace(/,/g, ''), 10) || 0;

  const handleParcelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/,/g, ''); // Remove existing commas
    const num = parseInt(value);
    if (!isNaN(num)) {
      setParcels(num.toLocaleString());
    } else if (value === '') {
      setParcels('');
    }
  };

  return (
    <div className="relative min-h-[80vh] bg-linear-to-br from-blue-600 via-blue-700 to-indigo-800 text-white overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-black opacity-20"></div>
      <div className="absolute inset-0 bg-[url('/api/placeholder/1920/1080')] bg-cover bg-center opacity-30"></div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Main Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <Badge className="bg-white/20 text-white border-white/40 hover:bg-white/30 hover:border-white/50 shadow-md backdrop-blur-xs transition-all duration-200">
                <Mountain className="w-4 h-4 mr-2" />
                Built for Montana Communities
              </Badge>

              <h1 className="text-4xl md:text-6xl font-bold leading-tight">
                Transform Your
                <span className="block text-blue-200">Montana Community</span>
              </h1>

              <p className="text-xl md:text-2xl text-blue-100 max-w-2xl">
                Modern governance, transparent finances, and efficient operations for rural
                communities. Simple per-parcel pricing with no hidden fees.
              </p>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap gap-6 text-blue-100">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                <span> Population Capacity Unlimited</span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                <span>85% Time Savings vs Competitors</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-white text-blue-700 hover:bg-blue-50 font-semibold"
                onClick={onGetStarted}
              >
                Get Started Today
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                size="lg"
                className="bg-white/20 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-blue-700 font-semibold"
                onClick={onScheduleDemo}
              >
                Schedule Demo
              </Button>
            </div>

            {/* Selected Add-ons Summary Chip */}
            {selectedAddOnNames && selectedAddOnNames.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
                <span className="text-blue-100/90">Selected add-ons:</span>
                {selectedAddOnNames.slice(0, 3).map((name) => (
                  <Badge
                    key={name}
                    className="bg-white/20 text-white border-white/40 hover:bg-white/30"
                  >
                    {name}
                  </Badge>
                ))}
                {selectedAddOnNames.length > 3 && (
                  <Badge className="bg-white/10 text-white border-white/30">
                    +{selectedAddOnNames.length - 3} more
                  </Badge>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Pricing Calculator */}
          <div className="lg:pl-8">
            <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-2xl">
              <CardContent className="p-8">
                <div className="space-y-6">
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      Calculate Your Pricing
                    </h3>
                    <p className="text-gray-600">See exactly what your community will pay</p>
                  </div>

                  {/* Parcel Input */}
                  <div className="space-y-2">
                    <Label htmlFor="parcels" className="text-gray-700 font-medium">
                      Number of Voting Parcels
                    </Label>
                    <Input
                      id="parcels"
                      type="text"
                      value={parcels}
                      onChange={handleParcelChange}
                      className="text-lg text-center font-semibold"
                    />
                  </div>

                  {/* Pricing Results */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3 text-center">
                      {/* Silver - Higher Contrast & Edge Accent */}
                      <div className="group relative overflow-hidden rounded-xl p-4 border border-zinc-300/70 bg-[linear-gradient(128deg,#F5F7FA_0%,#E9EBEF_52%,#DADBDF_100%)] shadow-[0_1px_2px_rgba(0,0,0,0.05),0_6px_18px_-6px_rgba(0,0,0,0.12)] ring-1 ring-white/70 backdrop-blur-xs transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_6px_14px_-4px_rgba(0,0,0,0.10),0_18px_42px_-10px_rgba(0,0,0,0.18)]">
                        <div className="pointer-events-none absolute inset-0 rounded-xl bg-[radial-gradient(circle_at_28%_18%,rgba(255,255,255,0.75),rgba(255,255,255,0)_70%)] opacity-80"></div>
                        <div className="pointer-events-none absolute -inset-px rounded-[14px] bg-linear-to-br from-white/60 via-transparent to-white/10 mix-blend-overlay"></div>
                        <div className="pointer-events-none absolute top-0 inset-x-0 h-[2px] bg-linear-to-r from-transparent via-white/70 to-transparent"></div>
                        <div className="relative z-10">
                          <div className="text-sm font-semibold text-zinc-800 mb-1 tracking-wide">
                            Silver
                          </div>
                          <div className="text-[1.35rem] leading-none font-bold bg-clip-text text-transparent bg-[linear-gradient(90deg,#2F3033_0%,#1E1F21_60%,#3A3B3E_100%)] drop-shadow-xs">
                            $5
                          </div>
                          <div className="text-[11px] uppercase font-medium tracking-wide text-zinc-600/90 mt-0.5">
                            /parcel/month
                          </div>
                          <div className="text-xs text-zinc-700 mt-3">
                            Total: {formatCurrency(pricing.silver.monthly)}
                          </div>
                          <div className="text-[11px] text-zinc-700 mt-3 space-y-0.5">
                            <div className="font-medium">
                              Setup Fee: {formatCurrency(pricing.silver.setup)}
                            </div>
                            <div className="text-zinc-600 flex items-center justify-center gap-1">
                              (one-time; $30/parcel)
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger
                                    aria-label="What is the setup fee?"
                                    className="inline-flex items-center text-zinc-600 hover:text-zinc-700 transition-colors"
                                  >
                                    <Info className="w-3.5 h-3.5" />
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs text-left">
                                    One-time setup fee for platform configuration and onboarding.
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Gold - Warmer, Richer, Still Subtle */}
                      <div className="group relative overflow-hidden rounded-xl p-4 border border-amber-300/70 bg-[linear-gradient(135deg,#F0E6D2_0%,#F3E3C6_48%,#E6CFAB_100%)] shadow-[0_1px_2px_rgba(0,0,0,0.05),0_6px_20px_-8px_rgba(0,0,0,0.15)] ring-1 ring-white/70 backdrop-blur-xs transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_-6px_rgba(0,0,0,0.12),0_22px_46px_-14px_rgba(0,0,0,0.22)]">
                        <div className="pointer-events-none absolute inset-0 rounded-xl bg-[radial-gradient(circle_at_30%_16%,rgba(255,255,255,0.85),rgba(255,255,255,0)_70%)]"></div>
                        <div className="pointer-events-none absolute -inset-px rounded-[14px] bg-[linear-gradient(145deg,rgba(255,255,255,0.65)_0%,rgba(255,255,255,0)_60%)] mix-blend-overlay"></div>
                        <div className="pointer-events-none absolute top-0 inset-x-0 h-[2px] bg-linear-to-r from-transparent via-white/80 to-transparent"></div>
                        <div className="relative z-10">
                          <div className="text-sm font-semibold text-stone-800 mb-1 tracking-wide">
                            Gold
                          </div>
                          <div className="text-[1.35rem] leading-none font-bold bg-clip-text text-transparent bg-[linear-gradient(90deg,#4B3A1E_0%,#2F2414_55%,#5A4525_100%)] drop-shadow-xs">
                            $8
                          </div>
                          <div className="text-[11px] uppercase font-medium tracking-wide text-stone-600/90 mt-0.5">
                            /parcel/month
                          </div>
                          <div className="text-xs text-stone-700 mt-3">
                            Total: {formatCurrency(pricing.gold.monthly)}
                          </div>
                          <div className="text-[11px] text-stone-700 mt-3 space-y-0.5">
                            <div className="font-medium">
                              Setup Fee: {formatCurrency(pricing.gold.setup)}
                            </div>
                            <div className="text-stone-600 flex items-center justify-center gap-1">
                              (one-time; $60/parcel)
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger
                                    aria-label="What is the setup fee?"
                                    className="inline-flex items-center text-stone-600 hover:text-stone-700 transition-colors"
                                  >
                                    <Info className="w-3.5 h-3.5" />
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs text-left">
                                    One-time setup fee for platform configuration and onboarding.
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </div>
                          <Badge className="mt-3 inline-flex bg-[linear-gradient(92deg,#F1E1C7_0%,#FAF4E7_50%,#F1E1C7_100%)] text-stone-700 border border-amber-300/70 shadow-[0_0_0_1px_rgba(255,255,255,0.65)_inset,0_1px_2px_rgba(0,0,0,0.05)] hover:shadow-[0_0_0_1px_rgba(255,255,255,0.7)_inset,0_2px_5px_rgba(0,0,0,0.10)] hover:text-stone-800 text-[10px] tracking-wide font-semibold px-2.5 py-1 rounded-full transition-all">
                            Popular
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <div className="text-center text-sm text-gray-600">
                      Pricing for{' '}
                      <span className="font-semibold">{formatNumber(parcelsNumber)} parcels</span>
                      <br />
                      3-year minimum term (Silver/Gold)
                      <br />
                      Setup fees exclude analog-to-digital migration and governance mirroring; these
                      are add-on services determined during pre-signing scoping
                    </div>

                    {/* Honest, Transparent Pricing Message */}
                    <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="text-center">
                        <div className="text-sm font-semibold text-green-800 mb-2">
                          🚫 No Surprise Bills. Ever.
                        </div>
                        <div className="text-xs text-green-700 leading-relaxed">
                          We believe in transparency. Your monthly bill has two clear components:
                          <br />
                          <span className="font-medium">✅ Platform Fee</span> - Fixed cost for
                          software features and support
                          <br />
                          <span className="font-medium">✅ Usage Fee</span> - Only pay for the data
                          your community actually uses
                          <br />
                          <br />
                          <span className="font-semibold">
                            Most communities see usage costs of $0.25-$1.00 per parcel monthly.
                          </span>{' '}
                          We provide detailed usage forecasts during onboarding and monthly reports
                          so you always know what to expect.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-linear-to-t from-white to-transparent"></div>
    </div>
  );
};
